import { JwtPayload, jwtDecode } from 'jwt-decode';

import { TokenVerificationError } from '../errors/AuthErrors';
import { User } from '../entities/User';

/**
 * Domain service for authentication-related operations
 * Contains pure domain logic related to authentication
 */
export class AuthDomainService {
  /**
   * Verifies if a token is valid and not expired
   */
  verifyToken(token: string): boolean {
    if (!token) return false;

    try {
      const decoded = jwtDecode<JwtPayload>(token);
      return Boolean(decoded.exp && decoded.exp > Date.now() / 1000);
    } catch {
      throw new TokenVerificationError();
    }
  }

  /**
   * Extracts user information from JWT token
   */
  extractUserFromToken(token: string): User | null {
    if (!token) return null;

    try {
      const decoded = jwtDecode<
        JwtPayload & {
          userName?: string;
          dbName?: string;
          preferred_username?: string;
          DbName?: string;
          TermsAccepted?: string;
          termsAccepted?: string;
          TimezoneName?: string;
          timezoneName?: string;
          UserEmail?: string;
          userEmail?: string;
          userId?: string;
          UserId?: string;
          CrmLicense?: string;
          CrmAdmin?: string;
          OrganizationID?: string;
        }
      >(token);

      // Normalize properties to handle different naming conventions
      // TODO: Check if this is needed in the future
      decoded.userName = decoded?.userName ?? decoded?.preferred_username ?? '';
      decoded.dbName = decoded?.dbName ?? decoded?.DbName ?? '';
      decoded.termsAccepted = decoded?.TermsAccepted ?? decoded?.termsAccepted ?? 'false';
      decoded.timezoneName = decoded?.TimezoneName ?? decoded?.timezoneName ?? '';
      decoded.userEmail = decoded?.UserEmail ?? decoded?.userEmail ?? '';
      decoded.userId = decoded?.UserId ?? decoded?.userId ?? '';

      if (!decoded.userName) return null;

      // Exact-match comparison only — mirrors CRMAPI's CrmAccessAuthorizationHandler
      // (string.Equals(crmLicense, "true", Ordinal)). Do NOT use Boolean(str) here:
      // Boolean('false') is true in JS, which would silently treat a malformed or
      // explicit "false" claim as licensed.
      const crmLicense = decoded.CrmLicense === 'true';
      const crmAdmin = decoded.CrmAdmin === 'true';
      const organizationId = decoded.OrganizationID ?? undefined;

      return {
        userName: decoded.userName,
        dbName: decoded.dbName,
        termsAccepted: Boolean(decoded.termsAccepted),
        timezoneName: decoded.timezoneName,
        userEmail: decoded.userEmail,
        userId: decoded.userId,
        crmLicense,
        crmAdmin,
        organizationId
      };
    } catch {
      return null;
    }
  }

  /**
   * Checks if the provided credentials meet security requirements
   */
  validateCredentialStrength(password: string): {
    isValid: boolean;
    message?: string;
  } {
    if (password.length < 8) {
      return {
        isValid: false,
        message: 'Password must be at least 8 characters long'
      };
    }

    // Additional validation logic can be added here
    return { isValid: true };
  }
}
